import { useState, useEffect, useRef, useCallback } from 'react';
import './notification-bell.css';
import { authFetch, getDomain } from '../utils/apis/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

const formatDate = (iso) => {
  const timezone = sessionStorage.getItem('timezone') || undefined;

  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
};

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const BASE_URL = getDomain();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_URL}notifications/unread-count`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCount(data.unread_count ?? 0);
    } catch (_) { }
  }, [BASE_URL]);

  // Poll unread count every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${BASE_URL}notifications`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.items || []);
    } catch (_) { }
    finally { setLoading(false); }
  }, [BASE_URL]);

  const handleBellClick = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications();
  };

  const markOne = async (notifId) => {
    try {
      await authFetch(`${BASE_URL}notifications/${notifId}/read`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) { }
  };

  const markAll = async () => {
    try {
      await authFetch(`${BASE_URL}notifications/read-all`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) { }
  };

  const hasUnread = notifications.some((n) => !n.is_read);

  return (
    <div className="nb-wrapper" ref={panelRef}>
      <button className="nb-bell" onClick={handleBellClick} aria-label="Notifications">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2M8 1.918l-.797.161A4 4 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4 4 0 0 0-3.203-3.92zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6a5 5 0 0 1 10 0c0 .88.32 4.2 1.22 6" />
        </svg>
        {unreadCount > 0 && (
          <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="nb-panel">
          <div className="nb-panel-header">
            <span className="nb-panel-title">Notifications</span>
            {hasUnread && (
              <button className="nb-mark-all" onClick={markAll}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="nb-panel-body">
            {loading ? (
              <p className="nb-empty">Loading...</p>
            ) : notifications.length === 0 ? (
              <p className="nb-empty">You're all caught up!</p>
            ) : (
              <ul className="nb-list">
                {notifications.map((n) => (
                  <li key={n.id} className={`nb-item${n.is_read ? ' nb-item-read' : ''}`}>
                    {!n.is_read && <span className="nb-dot" />}
                    <div className="nb-item-body">
                      <div className="nb-item-title">{n.title}</div>
                      <div className="nb-item-message">{n.message}</div>
                      <div className="nb-item-meta">{formatDate(n.created_at)}</div>
                    </div>
                    {!n.is_read && (
                      <button
                        className="nb-mark-one"
                        onClick={() => markOne(n.notification_id)}
                        title="Mark as read"
                      >
                        &#10003;
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
